import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../../util/Toast';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../util/SettingsProvider';
export const Wallet = () => {
  const [walletData, setWalletData] = useState({
    balance: '0.00',
    transactions: [],
    totalPages: 1,
    currentPage: 1
  });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  // Modal & Form States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [upiId, setUpiId] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };
  const settings = useSettings();
  useEffect(() => { document.title = `Wallet | ${settings?.settings?.site_name || ''}`; }, [settings?.settings?.site_name]);
  
  const fetchWalletData = useCallback(async (currentPage = 1) => {
    const token = getCookie('authToken');
    try {
      setLoading(true);
      const baseUrl = process.env.REACT_APP_API_URL || '';

      const response = await axios.get(`${baseUrl}/api/wallet`, {
        params: {
          page: currentPage
        },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data && response.data.data) {
        const apiData = response.data.data;
        const rawTransactions = Array.isArray(apiData.transactions)
          ? apiData.transactions
          : [];

        // Format backend transactions to match visual date pattern (e.g., 25 Jul 2026 • 03:44 pm)
        const formattedTxns = rawTransactions.map((tx, idx) => {
          let dateStr = tx.date || '';
          if (tx.time) {
            dateStr += ` • ${tx.time}`;
          }
          return {
            id: tx.id || idx,
            title: tx.title,
            amount: tx.amount,
            date: dateStr || 'Recently',
            status: tx.status
          };
        });

        setWalletData({
          balance: apiData.balance !== undefined ? String(apiData.balance) : '0.00',
          transactions: formattedTxns,
          totalPages: apiData.totalPages || 1,
          currentPage: apiData.currentPage || currentPage
        });
      }
    } catch (error) {
      console.error('Error fetching wallet data:', error);
      toast.error(error.response?.data?.msg || "An unexpected error occurred.");
      if (error.response && error.response.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchWalletData(page);
  }, [page, fetchWalletData]);

  // Modal Handlers
  const handleOpenModal = () => {
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setUpiId('');
    setWithdrawAmount('');
    setFormErrors({});
  };

  // Client-side Validation Logic
  const validateForm = () => {
    const errors = {};
    const upiRegex = /^[\w.-]+@[\w.-]+$/;
    const numericBalance = parseFloat(walletData.balance || 0);
    const numericAmount = parseFloat(withdrawAmount);

    if (!upiId.trim()) {
      errors.upiId = 'UPI ID is required.';
    } else if (!upiRegex.test(upiId.trim())) {
      errors.upiId = 'Enter a valid UPI ID (e.g. name@upi or user@okaxis).';
    }

    if (!withdrawAmount) {
      errors.withdrawAmount = 'Amount is required.';
    } else if (isNaN(numericAmount) || numericAmount <= 0) {
      errors.withdrawAmount = 'Enter a valid amount greater than ₹0.';
    } else if (numericAmount > numericBalance) {
      errors.withdrawAmount = `Amount exceeds available balance (₹${walletData.balance}).`;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleWithdrawSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const token = getCookie('authToken');
    try {
      setSubmitting(true);
      const baseUrl = process.env.REACT_APP_API_URL || '';

      // POST call hitting the backend service route with Authorization header
      await axios.post(`${baseUrl}/api/wallet/withdraw`, {
        upi: upiId.trim(),
        amount: parseFloat(withdrawAmount)
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Update state locally for immediate feedback
      const deductedAmount = parseFloat(withdrawAmount);
      const updatedBalance = Math.max(0, parseFloat(walletData.balance || 0) - deductedAmount).toFixed(2);

      const now = new Date();
      const newTxn = {
        id: Date.now(),
        title: 'Withdrawal to UPI',
        amount: deductedAmount.toLocaleString(),
        date: `${now.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} • ${now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`,
        status: 'pending'
      };

      setWalletData((prev) => ({
        ...prev,
        balance: updatedBalance,
        transactions: [newTxn, ...prev.transactions]
      }));

      handleCloseModal();
    } catch (err) {
      console.error('Error submitting withdrawal:', err);
      // Parse backend error messages thrown by createWithdrawRequestService
      const backendMessage = err.response?.data?.message || err.message;

      if (backendMessage && backendMessage.toLowerCase().includes('minimum')) {
        setFormErrors({ withdrawAmount: backendMessage });
      } else if (backendMessage && backendMessage.toLowerCase().includes('balance')) {
        setFormErrors({ withdrawAmount: backendMessage });
      } else {
        setFormErrors({ general: backendMessage || 'Failed to process request. Please try again.' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Dynamic Status Badge Helper
  const renderStatusBadge = (status) => {
    const statusLower = (status || '').toLowerCase();

    if (statusLower === 'approved' || statusLower === 'success') {
      return (
        <span className="px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
          {status}
        </span>
      );
    }

    if (statusLower === 'rejected' || statusLower === 'failed') {
      return (
        <span className="px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider bg-rose-500/10 text-rose-400 border border-rose-500/20">
          {status}
        </span>
      );
    }

    return (
      <span className="px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider bg-amber-500/10 text-amber-400 border border-amber-500/20">
        {status || 'pending'}
      </span>
    );
  };

  return (
    <div className="w-full pt-16 sm:pt-24 lg:pt-28 pb-16 px-4 sm:px-8 lg:pl-80 relative overflow-hidden font-sans">
      <style>{`
        @keyframes springIn {
          0% { opacity: 0; transform: translateY(16px) scale(0.98); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }

        @keyframes modalSpring {
          0% { opacity: 0; transform: scale(0.92) translateY(20px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }

        @keyframes pulseGlow {
          0%, 100% { opacity: 0.15; transform: scale(1); }
          50% { opacity: 0.35; transform: scale(1.08); }
        }

        .animate-spring-in {
          animation: springIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .animate-modal-spring {
          animation: modalSpring 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .ambient-glow {
          animation: pulseGlow 8s ease-in-out infinite;
        }

        .micro-interaction {
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .micro-interaction:hover {
          transform: translateY(-2px);
        }

        .micro-interaction:active {
          transform: translateY(0px) scale(0.98);
        }
      `}</style>

      {/* Ambient Background Lighting */}
      <div className="absolute top-10 left-1/3 w-96 h-96 bg-emerald-500/10 rounded-full blur-[120px] pointer-events-none ambient-glow" />
      <div className="absolute bottom-10 right-10 w-80 h-80 bg-teal-500/10 rounded-full blur-[100px] pointer-events-none ambient-glow" />

      <div className="max-w-[1400px] mx-auto space-y-8 relative z-10">

        {/* Balance Hero Card */}
        <div className="bg-zinc-900/90 backdrop-blur-md rounded-[2.5rem] sm:rounded-[3rem] p-8 sm:p-12 border border-zinc-800/80 shadow-2xl relative overflow-hidden group animate-spring-in">
          <div className="absolute top-[-20%] right-[-10%] w-64 h-64 bg-emerald-500/10 rounded-full blur-[80px]"></div>
          <div className="absolute bottom-[-20%] left-[-5%] w-48 h-48 bg-teal-500/10 rounded-full blur-[60px]"></div>

          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-8">
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-emerald-400">
                <div className="p-2 bg-emerald-500/10 rounded-xl">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
                <span className="text-xs font-black uppercase tracking-[0.2em]">Total Available Funds</span>
              </div>

              <h2 className="text-5xl sm:text-6xl font-black text-zinc-100 tracking-tighter">
                {loading ? '...' : `₹${walletData.balance}`}
              </h2>

              <div className="flex items-center gap-2 text-emerald-400 bg-emerald-500/10 w-fit px-3.5 py-1.5 rounded-xl border border-emerald-500/20">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                <span className="text-[11px] font-extrabold">Live Balance</span>
              </div>
            </div>

            <button
              onClick={handleOpenModal}
              className="group flex items-center justify-center gap-3 px-8 py-5 bg-emerald-500 hover:bg-emerald-400 rounded-3xl text-zinc-950 font-black text-lg transition-all duration-300 shadow-xl shadow-emerald-500/10 micro-interaction"
            >
              Withdraw Now
              <svg className="w-5 h-5 group-hover:rotate-45 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M7 17L17 7M17 7H7M17 7V17" />
              </svg>
            </button>
          </div>
        </div>

        {/* Transaction History Section */}
        <div className="bg-zinc-900/80 backdrop-blur-md border border-zinc-800/80 rounded-[2.5rem] shadow-xl overflow-hidden animate-spring-in">

          <div className="p-6 sm:p-8 border-b border-zinc-800/80 flex items-center justify-between">
            <h3 className="text-xl font-black tracking-tight text-zinc-100 flex items-center gap-3">
              <span className="w-2 h-7 bg-emerald-500 rounded-full"></span>
              Transaction History
            </h3>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-zinc-500 text-sm font-semibold tracking-wide">Loading transactions...</p>
            </div>
          ) : walletData.transactions.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-zinc-400 text-sm font-semibold">No transactions recorded yet.</p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-800/50">
              {walletData.transactions.map((tx) => (
                <div
                  key={tx.id || tx.date}
                  className="flex items-center justify-between p-5 sm:p-6 hover:bg-zinc-800/30 transition-colors group"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-zinc-950 rounded-2xl flex items-center justify-center text-zinc-400 border border-zinc-800 group-hover:text-emerald-400 group-hover:border-emerald-500/30 transition-all">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-bold text-zinc-100 text-base">{tx.title}</p>
                      <p className="text-[11px] font-bold text-zinc-500 mt-0.5">{tx.date}</p>
                    </div>
                  </div>

                  <div className="text-right flex flex-col items-end gap-1.5">
                    <p className="font-black text-zinc-100 text-lg tracking-tight">
                      ₹{tx.amount}
                    </p>
                    {renderStatusBadge(tx.status)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination Footer */}
          <div className="p-6 bg-zinc-950/40 border-t border-zinc-800/80 flex items-center justify-between">
            <p className="text-xs font-bold text-zinc-500 uppercase tracking-widest">
              Page {walletData.currentPage} of {walletData.totalPages}
            </p>

            <div className="flex gap-2">
              <button
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                className="p-2.5 bg-zinc-900 border border-zinc-800 text-zinc-300 rounded-xl disabled:opacity-30 disabled:hover:border-zinc-800 hover:border-emerald-500/50 hover:text-emerald-400 transition-all micro-interaction"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                disabled={page >= walletData.totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
                className="p-2.5 bg-zinc-900 border border-zinc-800 text-zinc-300 rounded-xl disabled:opacity-30 disabled:hover:border-zinc-800 hover:border-emerald-500/50 hover:text-emerald-400 transition-all micro-interaction"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

        </div>

      </div>

      {/* WITHDRAWAL MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6">
          {/* Backdrop Blur Overlay */}
          <div
            onClick={handleCloseModal}
            className="fixed inset-0 bg-zinc-950/80 backdrop-blur-md transition-opacity duration-300"
          />

          {/* Modal Container */}
          <div className="relative w-full max-w-md bg-zinc-900/95 border border-zinc-800 rounded-[2.5rem] p-6 sm:p-8 shadow-2xl z-10 overflow-hidden animate-modal-spring">
            {/* Background Ambient Glow */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-2xl pointer-events-none"></div>

            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-emerald-500/10 text-emerald-400 rounded-2xl border border-emerald-500/20">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-black text-zinc-100 tracking-tight">Withdraw Funds</h3>
              </div>

              <button
                onClick={handleCloseModal}
                className="p-2 rounded-xl bg-zinc-950/60 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 border border-zinc-800 transition-all micro-interaction"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {formErrors.general && (
              <div className="mb-4 p-3 bg-rose-500/10 border border-rose-500/20 rounded-xl text-rose-400 text-xs font-semibold">
                {formErrors.general}
              </div>
            )}

            <form onSubmit={handleWithdrawSubmit} className="space-y-5">

              {/* UPI Field */}
              <div className="space-y-2">
                <label className="text-xs font-black uppercase tracking-wider text-zinc-400 block">
                  UPI ID
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={upiId}
                    onChange={(e) => {
                      setUpiId(e.target.value);
                      if (formErrors.upiId) setFormErrors((prev) => ({ ...prev, upiId: null }));
                    }}
                    placeholder="username@upi"
                    className={`w-full px-4 py-3.5 bg-zinc-950/80 border ${formErrors.upiId ? 'border-rose-500/80 focus:ring-rose-500' : 'border-zinc-800 focus:border-emerald-500/60'
                      } rounded-2xl text-zinc-100 placeholder-zinc-600 outline-none text-sm font-semibold transition-all shadow-inner`}
                  />
                </div>
                {formErrors.upiId && (
                  <p className="text-rose-400 text-xs font-semibold mt-1 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {formErrors.upiId}
                  </p>
                )}
              </div>

              {/* Amount Field */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-black uppercase tracking-wider text-zinc-400 block">
                    Amount (₹)
                  </label>
                  <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-lg border border-emerald-500/20">
                    Avail: ₹{walletData.balance}
                  </span>
                </div>
                <div className="relative">
                  <input
                    type="number"
                    step="any"
                    value={withdrawAmount}
                    onChange={(e) => {
                      setWithdrawAmount(e.target.value);
                      if (formErrors.withdrawAmount) setFormErrors((prev) => ({ ...prev, withdrawAmount: null }));
                    }}
                    placeholder="Enter amount"
                    className={`w-full px-4 py-3.5 bg-zinc-950/80 border ${formErrors.withdrawAmount ? 'border-rose-500/80 focus:ring-rose-500' : 'border-zinc-800 focus:border-emerald-500/60'
                      } rounded-2xl text-zinc-100 placeholder-zinc-600 outline-none text-sm font-semibold transition-all shadow-inner`}
                  />
                </div>
                {formErrors.withdrawAmount && (
                  <p className="text-rose-400 text-xs font-semibold mt-1 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {formErrors.withdrawAmount}
                  </p>
                )}
              </div>

              {/* Preset Amount Buttons */}
              <div className="flex items-center gap-2 pt-1">
                {['100', '250', walletData.balance].map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => {
                      setWithdrawAmount(preset);
                      if (formErrors.withdrawAmount) setFormErrors((prev) => ({ ...prev, withdrawAmount: null }));
                    }}
                    className="flex-1 py-1.5 bg-zinc-950 border border-zinc-800 text-zinc-300 rounded-xl text-xs font-bold hover:border-emerald-500/40 hover:text-emerald-400 transition-all micro-interaction"
                  >
                    ₹{preset}
                  </button>
                ))}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitting}
                className="w-full mt-4 py-4 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 rounded-2xl font-black text-base shadow-lg shadow-emerald-500/10 active:scale-95 transition-all micro-interaction flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {submitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-zinc-950 border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    Submit Withdrawal Request
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </>
                )}
              </button>

            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default Wallet;